import { FC } from 'react';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { Estimate, isValidEstimate } from '@zoopla/estimates';
import { Spinner } from '@zoopla-eng/zds-components/icons';
import { SaleEstimate, formatPostcode } from '../../helpers';
import {
    addressStyle,
    estimateWrapperStyle,
    leadStyle,
    loaderStyle,
    wrapperStyle,
} from './InstantValuation.styles.css';

const HEADING_ID = 'val-success-estimate-heading';

type InstantValuationProps = {
    saleEstimate?: SaleEstimate;
    fullAddress: string;
    postcode: string;
    isLoading: boolean;
};

const InstantValuation: FC<InstantValuationProps> = ({
    saleEstimate,
    fullAddress,
    postcode,
    isLoading,
}) => {
    if (isLoading) {
        return (
            <section className={`${wrapperStyle} ${loaderStyle}`}>
                <Spinner role="presentation" />
                <p>Loading instant valuation…</p>
            </section>
        );
    }

    const validEstimate = isValidEstimate(saleEstimate);

    const headingText = validEstimate
        ? 'Your instant valuation'
        : 'Instant valuation unavailable';

    return (
        <section className={wrapperStyle} aria-labelledby={HEADING_ID}>
            {validEstimate && (
                <p className={`${leadStyle} ${fontGroups.knife}`}>
                    Based on data alone, and without us viewing your property,
                    this is what we think it’s worth…
                </p>
            )}
            <h2 className={fontGroups.mug} id={HEADING_ID}>
                {headingText}
            </h2>
            {validEstimate && (
                <p
                    className={`${addressStyle} ${fontGroups.ladle}`}
                    // This is ONLY to be used for testing that the address is
                    // not rendered in unit tests. Use testing-library best
                    // practices for instances where you are asserting that it
                    // is present
                    data-testid="instant-valuation-address"
                    data-cs-mask
                >
                    {fullAddress}
                </p>
            )}
            <div className={estimateWrapperStyle}>
                <Estimate
                    type="sale"
                    hideSubtitle
                    lowerPrice={saleEstimate?.lowerPrice || 0}
                    currentPrice={saleEstimate?.currentPrice || 0}
                    upperPrice={saleEstimate?.upperPrice || 0}
                    confidenceLevel={saleEstimate?.confidenceLevel}
                    errorLink={{
                        text: `Check sold prices in ${formatPostcode(
                            postcode
                        )}`,
                        href: `/house-prices/?q=${postcode}`,
                    }}
                />
            </div>
        </section>
    );
};

export default InstantValuation;
